import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { useId, useMemo, useState } from "react";
import { createDateFormatter, useDisplayTimezone } from "@nexus/ui";
import { firstError, unwrap, Widget, HeaderStats } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { CATEGORY_HEATMAP_TABLE_CONTRACT } from "./types";
import type { CategoryHeatmapTableData, HeatmapColumn, HeatmapItem } from "./types";

export interface CategoryHeatmapTableConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "category-heatmap-table",
  name: "Category heatmap table",
  description:
    "Cross-tab of per-row counts across labeled column groups, tinted by relative count, with a hover breakdown and totals.",
  version: "1.0.0",
  dataRequests: {
    table: {
      source: "category-heatmap-table",
      contract: CATEGORY_HEATMAP_TABLE_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: CategoryHeatmapTableConfig = {};

const countFormat = new Intl.NumberFormat("en");

function flatColumns(data: CategoryHeatmapTableData): HeatmapColumn[] {
  return data.columnGroups.flatMap((group) => group.columns);
}

function tint(token: string, ratio: number, floor: number, ceil: number): string | undefined {
  if (ratio <= 0) return undefined;
  const pct = Math.round(floor + ratio * (ceil - floor));
  return `color-mix(in srgb, var(${token}) ${pct}%, transparent)`;
}

type TipState = { x: number; y: number; title: string; items: HeatmapItem[] } | null;

function HeatCell({
  items,
  column,
  heatMax,
  bordered,
  groupStart,
  title,
  onHover,
  tooltipId,
}: {
  items: HeatmapItem[];
  column: HeatmapColumn;
  heatMax: number;
  bordered: boolean;
  groupStart: boolean;
  title: string;
  onHover: (tip: TipState) => void;
  tooltipId: string;
}) {
  const count = items.length;
  const interactive = count > 0;
  const bg = column.heat ? tint(column.colorToken, count / heatMax, 14, 66) : undefined;
  const color = !column.heat && interactive ? `var(${column.colorToken})` : undefined;

  return (
    <td
      className={`text-center text-sm font-medium tabular-nums transition-colors ${bordered ? "border-b border-border" : ""} ${groupStart ? "border-l border-border" : ""} ${!interactive ? "px-3 py-2 text-muted" : ""}`}
      style={{ backgroundColor: bg, color }}
    >
      {interactive ? (
        <button
          type="button"
          aria-label={`${title}: ${count}. Show details`}
          aria-describedby={tooltipId}
          className="h-full w-full px-3 py-2 font-medium tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
          onMouseEnter={(event) => onHover({ x: event.clientX, y: event.clientY, title, items })}
          onMouseMove={(event) => onHover({ x: event.clientX, y: event.clientY, title, items })}
          onMouseLeave={() => onHover(null)}
          onFocus={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            onHover({ x: rect.right, y: rect.top, title, items });
          }}
          onBlur={() => onHover(null)}
        >
          {count}
        </button>
      ) : (
        "–"
      )}
    </td>
  );
}

export function Component({ data, config }: WidgetProps<CategoryHeatmapTableConfig>) {
  const timezone = useDisplayTimezone();
  const dateTimeFormat = createDateFormatter(
    timezone,
    { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
    "en",
  );
  const error = firstError(data, ["table"]);
  const result = unwrap<CategoryHeatmapTableData>(data.table);
  const loading = data.table?.isLoading && !result;
  const heading = config.title ?? manifest.name;
  const [tip, setTip] = useState<TipState>(null);
  const tooltipId = useId();

  const columns = useMemo(() => (result ? flatColumns(result) : []), [result]);
  const groupStartKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const group of result?.columnGroups ?? []) {
      if (group.columns[0]) keys.add(group.columns[0].key);
    }
    return keys;
  }, [result]);

  const rows = useMemo(() => {
    const list = result?.rows ?? [];
    const totals = list.map((row) =>
      columns.reduce((sum, column) => sum + (row.cells[column.key]?.length ?? 0), 0),
    );
    return list
      .map((row, index) => ({ row, total: totals[index] }))
      .sort((a, b) => b.total - a.total);
  }, [result, columns]);

  const heatMax = useMemo(() => {
    let max = 1;
    for (const { row } of rows) {
      for (const column of columns) {
        if (!column.heat) continue;
        max = Math.max(max, row.cells[column.key]?.length ?? 0);
      }
    }
    return max;
  }, [rows, columns]);

  const grandTotal = rows.reduce((sum, { total }) => sum + total, 0);
  const columnTotals = columns.map((column) =>
    rows.reduce((sum, { row }) => sum + (row.cells[column.key]?.length ?? 0), 0),
  );

  return (
    <Widget
      title={heading}
      headerSummary={
        result?.summary?.length ? (
          <HeaderStats items={result.summary} label={`${heading} summary`} />
        ) : undefined
      }
    >
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="table" />
      ) : rows.length === 0 ? (
        <WidgetState title="No rows for this period." />
      ) : (
        <div className="p-2">
          <div className="divide-y divide-border @5xl/widget:hidden">
            {rows.map(({ row, total }) => (
              <article key={row.id} className="py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-semibold text-primary">{row.label}</h3>
                  <span className="text-xs tabular-nums text-muted">
                    {countFormat.format(total)}
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 text-xs @sm/widget:grid-cols-2 @3xl/widget:grid-cols-4">
                  {columns.map((column) => (
                    <div key={column.key} className="flex items-center justify-between gap-2">
                      <dt className="text-muted">{column.label}</dt>
                      <dd
                        className="font-semibold tabular-nums"
                        style={{ color: `var(${column.colorToken})` }}
                      >
                        {row.cells[column.key]?.length ?? 0}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-md border border-border @5xl/widget:block">
            <table className="w-full table-fixed border-separate border-spacing-0 text-primary">
              <thead className="bg-surface-elevated">
                <tr className="text-2xs font-semibold uppercase tracking-widest text-muted">
                  <th className="border-b border-border pb-2 text-left" colSpan={2} />
                  {result.columnGroups.map((group) => (
                    <th
                      key={group.id}
                      className="border-b border-l border-border px-3 py-2 text-center"
                      colSpan={group.columns.length}
                      scope="colgroup"
                    >
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr className="text-2xs font-semibold uppercase tracking-wider text-muted">
                  <th className="border-b border-border px-3 py-2 text-left" scope="col">
                    Row
                  </th>
                  <th className="border-b border-border px-3 py-2 text-center" scope="col">
                    Total
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`border-b border-border px-3 py-2 text-center ${groupStartKeys.has(column.key) ? "border-l border-l-border-strong" : ""}`}
                      scope="col"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ row, total }, rowIndex) => {
                  const bordered = rowIndex !== rows.length - 1;
                  return (
                    <tr key={row.id} className="transition-colors hover:bg-surface-elevated">
                      <td
                        className={`px-3 py-2 text-sm font-medium ${bordered ? "border-b border-border" : ""}`}
                      >
                        {row.label}
                      </td>
                      <td
                        className={`px-3 py-2 text-center text-sm font-semibold tabular-nums ${bordered ? "border-b border-border" : ""}`}
                      >
                        {total}
                      </td>
                      {columns.map((column) => (
                        <HeatCell
                          key={column.key}
                          items={row.cells[column.key] ?? []}
                          column={column}
                          heatMax={heatMax}
                          bordered={bordered}
                          groupStart={groupStartKeys.has(column.key)}
                          title={`${row.label} · ${column.label}`}
                          onHover={setTip}
                          tooltipId={tooltipId}
                        />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-surface-elevated">
                <tr className="text-sm font-semibold text-primary">
                  <td className="border-t border-border px-3 py-2 text-left text-2xs uppercase tracking-wide text-muted">
                    All rows
                  </td>
                  <td className="border-t border-border px-3 py-2 text-center tabular-nums">
                    {countFormat.format(grandTotal)}
                  </td>
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      className={`border-t border-border px-3 py-2 text-center tabular-nums ${groupStartKeys.has(column.key) ? "border-l border-l-border-strong" : ""}`}
                      style={!column.heat ? { color: `var(${column.colorToken})` } : undefined}
                    >
                      {columnTotals[index]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>

            {tip && (
              <div
                id={tooltipId}
                role="tooltip"
                className="pointer-events-none fixed z-50 w-64 rounded-md border border-border bg-surface p-3 shadow-lg"
                style={{
                  left: Math.min(tip.x + 16, window.innerWidth - 272),
                  top: tip.y + 16,
                }}
              >
                <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">
                  {tip.title}
                </p>
                <ul className="flex flex-col gap-2">
                  {tip.items.map((item, i) => (
                    <li key={`${item.timestamp}-${i}`} className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-primary">{item.label}</span>
                      <span className="text-2xs text-muted">
                        {dateTimeFormat.format(new Date(item.timestamp))}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<CategoryHeatmapTableConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
